import React from 'react'
import Header from "../components/Header";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import {useAuth} from "../authentication/AuthContext";
import {useDeleteNote, useDueNotes, useNotes} from "./hooks/useNote";
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import NotesSidebar from "./components/NotesSidebar";
import {useFolders} from "../folder/hooks/useFolder";
import {useProjects} from "../project/hooks/useProjects";

const NOTE_SCOPE = 'NOTE'
const FENCED_CODE_BLOCK_REGEX = /```([a-zA-Z0-9_-]+)?[ \t]*\n([\s\S]*?)```/g;

function toCompactPreview(value) {
    if (!value) return '';
    const withoutFences = value.replace(FENCED_CODE_BLOCK_REGEX, (_, __, code) => ` ${code ?? ''} `);
    return withoutFences.replace(/\s+/g, ' ').trim();
}

function extractPlainText(raw) {
    if (!raw) return '';
    try {
        const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const walk = (node) => {
            if (!node) return '';
            if (node.type === 'linebreak') return '\n';
            if (node.type === 'text') return node.text ?? '';
            const children = (node.children ?? []).map(walk).join('');

            if (['paragraph', 'heading', 'listitem', 'quote', 'code'].includes(node.type)) {
                return `${children}\n`;
            }

            return children;
        };
        return toCompactPreview(walk(json?.root ?? json));
    } catch {
        return toCompactPreview(raw);
    }
}

function Notes() {
    const {authToken} = useAuth();
    const navigate = useNavigate();
    const [pageSize, setPageSize] = React.useState(25);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [isSelectionMode, setIsSelectionMode] = React.useState(false);
    const [selectedNoteIds, setSelectedNoteIds] = React.useState(() => new Set());
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const {data: notes = []} = useNotes(authToken)
    const {data: dueNotes = []} = useDueNotes(authToken)
    const {mutateAsync: deleteNote, isLoading: isDeleting, error: deleteError, reset: resetDeleteError} = useDeleteNote(authToken)
    const {data: folders = []} = useFolders(authToken, NOTE_SCOPE)
    const {data: projects = []} = useProjects(authToken)
    const [searchParams] = useSearchParams()
    const activeFolder = searchParams.get("folder")
    const newNotePath = activeFolder ? `/note/new?folder=${activeFolder}` : '/note/new'
    const folderNameById = React.useMemo(
        () => new Map(folders.map((folder) => [folder.id, folder.name])),
        [folders]
    )
    const projectNameById = React.useMemo(
        () => new Map(projects.map((project) => [String(project.id ?? project.projectId ?? ''), project.name])),
        [projects]
    )

    const displayNotes = React.useMemo(
        () => activeFolder
            ? notes.filter(note => note.parentFolderId === activeFolder)
            : notes,
        [activeFolder, notes]
    )
    const totalPages = Math.max(1, Math.ceil(displayNotes.length / pageSize))
    const safeCurrentPage = Math.min(currentPage, totalPages)
    const pageStart = (safeCurrentPage - 1) * pageSize
    const paginatedNotes = displayNotes.slice(pageStart, pageStart + pageSize)
    const paginatedNoteIds = paginatedNotes.map((note) => String(note.id))
    const allPaginatedNotesSelected = paginatedNoteIds.length > 0
        && paginatedNoteIds.every((id) => selectedNoteIds.has(id))

    React.useEffect(() => {
        setCurrentPage(1)
        setIsSelectionMode(false)
        setSelectedNoteIds(new Set())
    }, [activeFolder])

    React.useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const formatTime = (iso) => {
        const d = new Date(iso)
        return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'}) +
            ' · ' + d.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'})
    }

    const toggleNoteSelection = (noteId) => {
        const normalizedId = String(noteId)
        setSelectedNoteIds((current) => {
            const next = new Set(current)
            if (next.has(normalizedId)) {
                next.delete(normalizedId)
            } else {
                next.add(normalizedId)
            }
            return next
        })
    }

    const toggleCurrentPageSelection = () => {
        setSelectedNoteIds((current) => {
            const next = new Set(current)
            if (allPaginatedNotesSelected) {
                paginatedNoteIds.forEach((id) => next.delete(id))
            } else {
                paginatedNoteIds.forEach((id) => next.add(id))
            }
            return next
        })
    }

    const toggleSelectionMode = () => {
        if (isDeleting) return

        setIsSelectionMode((current) => {
            if (current) {
                setSelectedNoteIds(new Set())
            }
            return !current
        })
    }

    const handleDeleteSelected = async () => {
        if (selectedNoteIds.size === 0 || isDeleting) return

        const idsToDelete = Array.from(selectedNoteIds)
        try {
            await Promise.all(idsToDelete.map((noteId) => deleteNote(noteId)))
            setSelectedNoteIds(new Set())
            setIsSelectionMode(false)
            setShowDeleteDialog(false)
        } catch {
            // The mutation exposes the error in the dialog and keeps the selection for retrying.
        }
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <NotesSidebar/>
                <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">

                    {/* Toolbar */}
                    <div className="mt-8 mb-8 px-8 flex items-center justify-between gap-4">
                        <button
                            type="button"
                            onClick={() => dueNotes[0] && navigate(`/note/${dueNotes[0].id}`)}
                            disabled={dueNotes.length === 0}
                            className="inline-flex items-center gap-2 rounded-xl bg-[var(--button-primary-bg)] px-6 py-3 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg transition-all duration-200 hover:translate-y-[-1px] hover:brightness-105 active:translate-y-0 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
                        >
                            Review Notes
                            {dueNotes.length > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">
                                    {dueNotes.length}
                                </span>
                            )}
                        </button>

                        <div className='flex gap-3 items-center'>
                            {isSelectionMode && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetDeleteError()
                                        setShowDeleteDialog(true)
                                    }}
                                    disabled={selectedNoteIds.size === 0 || isDeleting}
                                    className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-300 shadow-sm shadow-black/20 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <DeleteOutlineIcon fontSize="small"/>
                                    {isDeleting ? "Deleting..." : `Delete${selectedNoteIds.size > 0 ? ` (${selectedNoteIds.size})` : ""}`}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={toggleSelectionMode}
                                disabled={isDeleting}
                                aria-pressed={isSelectionMode}
                                className="inline-flex items-center rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] shadow-sm shadow-black/40 transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {isSelectionMode ? "Cancel" : "Select"}
                            </button>
                            <Link
                                to={newNotePath}
                                className="inline-flex items-center gap-2 rounded-md bg-[var(--surface-deep)] hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] border border-[var(--border-color)] shadow-sm shadow-black/40 transition"
                            >
                                <AddIcon fontSize="small"/>
                                New Note
                            </Link>
                        </div>
                    </div>

                    {dueNotes.length > 0 && (
                        <section className="mx-8 mb-8 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5">
                            <div className="mb-4 flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/15 text-amber-300">
                                    <EventRepeatOutlinedIcon fontSize="small"/>
                                </div>
                                <div>
                                    <h2 className="text-sm font-semibold text-amber-100">
                                        Due for review · {dueNotes.length}
                                    </h2>
                                    <p className="text-xs text-amber-200/55">
                                        Revisit these notes to keep them fresh.
                                    </p>
                                </div>
                            </div>
                            <ul className="grid gap-2 lg:grid-cols-2">
                                {dueNotes.map(note => (
                                    <li key={note.id}>
                                        <Link
                                            to={`/note/${note.id}`}
                                            className="flex items-center justify-between gap-4 rounded-xl border border-amber-500/15 bg-[var(--surface-deep)] px-4 py-3 transition hover:border-amber-400/35 hover:bg-[var(--surface-deep)]"
                                        >
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                                                    {note.title || 'Untitled note'}
                                                </p>
                                                <p className="mt-1 text-xs text-amber-300/70">
                                                    Due {formatTime(note.nextReviewDate)}
                                                </p>
                                            </div>
                                            <span className="shrink-0 text-xs text-amber-300">Review →</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Empty state */}
                    {displayNotes?.length === 0 && (
                        <div className='mt-16 flex justify-center px-6'>
                            <div className='w-full max-w-3xl flex flex-col items-center'>
                                <div
                                    className='group relative w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[var(--border-strong)] bg-gradient-to-b from-[var(--surface)] to-[var(--surface-deep)] px-10 py-20 text-center text-[var(--text-secondary)] backdrop-blur-sm'>
                                    <div className='absolute inset-0 rounded-xl pointer-events-none ring-1 ring-white/5 group-hover:ring-[var(--brand-coral)]/40 transition'/>
                                    <div className='flex items-center justify-center w-16 h-16 rounded-full bg-[var(--surface-hover)] text-[var(--brand-coral)] mb-6 shadow-inner'>
                                        <DescriptionOutlinedIcon fontSize='large'/>
                                    </div>
                                    <h3 className='text-xl font-semibold text-[var(--text-primary)] mb-2'>
                                        No notes here yet
                                    </h3>
                                    <p className='text-sm text-[var(--text-muted)] mb-6 max-w-md'>
                                        Start by creating a new note to jot down your thoughts and ideas.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => navigate(newNotePath)}
                                        className='inline-flex items-center gap-2 rounded-md bg-[var(--button-primary-bg)] px-5 py-2.5 text-sm font-semibold text-[var(--button-primary-text)] shadow-lg transition hover:brightness-105 active:brightness-95'
                                    >
                                        <AddIcon fontSize='small'/>
                                        New Note
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Note list */}
                    {displayNotes?.length > 0 && (
                        <div className='flex flex-1 flex-col px-8 pb-10'>
                            <div className="flex flex-1 flex-col gap-2">
                                {isSelectionMode && (
                                    <div className="flex items-center justify-between rounded-lg border border-[var(--border-color)] bg-[var(--surface)] px-4 py-2">
                                        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-[var(--text-muted)]">
                                            <input
                                                type="checkbox"
                                                checked={allPaginatedNotesSelected}
                                                onChange={toggleCurrentPageSelection}
                                                disabled={isDeleting}
                                                className="h-4 w-4 rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]"
                                            />
                                            Select this page
                                        </label>
                                        {selectedNoteIds.size > 0 && (
                                            <span className="text-xs text-[var(--brand-coral)]">{selectedNoteIds.size} selected</span>
                                        )}
                                    </div>
                                )}
                                <ul className="flex flex-col gap-2">
                                    {paginatedNotes.map(note => (
                                        <li key={note.id}>
                                            <div className={`flex w-full items-center gap-3 rounded-xl border bg-[var(--surface-deep)] px-4 py-4 transition-all hover:bg-[var(--surface-hover)] ${selectedNoteIds.has(String(note.id)) ? "border-[var(--brand-coral)]/60 ring-1 ring-[var(--brand-coral)]/30" : "border-transparent"}`}>
                                                {isSelectionMode && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedNoteIds.has(String(note.id))}
                                                        onChange={() => toggleNoteSelection(note.id)}
                                                        disabled={isDeleting}
                                                        aria-label={`Select ${note.title || "Untitled note"}`}
                                                        className="h-4 w-4 shrink-0 cursor-pointer rounded border-[var(--border-strong)] bg-[var(--surface-deep)] text-[var(--brand-coral)] focus:ring-[var(--brand-coral)]"
                                                    />
                                                )}
                                                <Link
                                                    to={`/note/${note.id}`}
                                                    className="group flex min-w-0 flex-1 gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-coral)]/50"
                                                >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="min-w-0 flex-1">
                                                            <h2 className="text-sm sm:text-base font-semibold text-[var(--text-primary)] leading-snug line-clamp-1">
                                                                {note.title}
                                                            </h2>
                                                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                                                {!activeFolder && note.parentFolderId && folderNameById.get(note.parentFolderId) && (
                                                                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--brand-coral)]/20 bg-[var(--brand-coral)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--brand-coral)]">
                                                                        <FolderOpenOutlinedIcon sx={{ fontSize: 12 }} />
                                                                        {folderNameById.get(note.parentFolderId)}
                                                                    </span>
                                                                )}
                                                                {note.projectId && (
                                                                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                                                                        <FolderOpenOutlinedIcon sx={{ fontSize: 12 }} />
                                                                        {projectNameById.get(String(note.projectId)) || String(note.projectId)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="shrink-0 text-[11px] text-[var(--text-muted)] tabular-nums">
                                                            {formatTime(note.updatedAt)}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-1">{extractPlainText(note.content)}</p>
                                                </div>
                                                <div className="self-center text-[11px] text-[var(--brand-coral)] opacity-0 group-hover:opacity-100 hidden sm:block">
                                                    Open →
                                                </div>
                                                </Link>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                        <span>Rows per page</span>
                                        <select
                                            value={pageSize}
                                            onChange={(event) => {
                                                setPageSize(Number(event.target.value))
                                                setCurrentPage(1)
                                            }}
                                            className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                            aria-label="Notes per page"
                                        >
                                            {[25, 50, 100].map(size => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                        <span>
                                            {pageStart + 1}–{Math.min(pageStart + pageSize, displayNotes.length)} of {displayNotes.length}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                            disabled={safeCurrentPage === 1}
                                            className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Previous
                                        </button>
                                        <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                            Page {safeCurrentPage} of {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                            disabled={safeCurrentPage === totalPages}
                                            className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </main>
            </div>
            {showDeleteDialog && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget && !isDeleting) {
                            setShowDeleteDialog(false)
                        }
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-notes-title"
                        aria-describedby="delete-notes-description"
                        className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-6 shadow-2xl shadow-black/50"
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-300">
                                <DeleteOutlineIcon/>
                            </div>
                            <div>
                                <h2 id="delete-notes-title" className="text-lg font-semibold text-[var(--text-primary)]">
                                    Delete selected notes?
                                </h2>
                                <p id="delete-notes-description" className="mt-2 text-sm leading-6 text-[var(--text-muted)]">
                                    {selectedNoteIds.size} selected note{selectedNoteIds.size === 1 ? "" : "s"} will be permanently deleted. This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        {deleteError && (
                            <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                Some selected notes could not be deleted. Please try again.
                            </p>
                        )}

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isDeleting}
                                className="rounded-lg border border-[var(--border-strong)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteSelected}
                                disabled={isDeleting || selectedNoteIds.size === 0}
                                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <DeleteOutlineIcon fontSize="small"/>
                                {isDeleting ? "Deleting..." : "Delete permanently"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Notes
